import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import MainClientRow from "./MainClientRow";
import {
  MainClientsFormFields,
  MainClientFormFields,
  mainClientsValidationSchema,
} from "./mainClientsValidationSchema";
import { CompanyProfileRequest, CompanyProfileResponse } from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./mainClientsForm.scss";

interface MainClientsFormProps {
  profile: CompanyProfileResponse;
  setIsDirty: (isDirty: boolean) => void;
}

const emptyMainClient: MainClientFormFields = {
  clientName: "",
  contactPersonName: "",
  contactEmail: "",
  contactPhone: "",
};

/**
 * The Main Clients wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon + React Hook
 * Form's `useFieldArray` (Task 10, Profile/Company flow) — the SAME
 * Addresses/BankingInformation/Certifications/Contacts (Tasks 8-10) rewrite shape: ONE
 * `mainClients` array, seeded from `profile.mainClients`, editable in place via `useFieldArray`
 * (`append`/`remove`), submitted whole. Real behaviour change from the pre-Carbon form (which
 * only ever let you ADD new rows or remove an unsaved one), safe for the identical reason
 * already established: `CompanyService#updateCompanyClients` (backend) already fully REPLACES the
 * main-clients list on every save (`company.getMainClients().clear()` then re-adds from the
 * request).
 *
 * `onSubmit` spreads `{...profile, ...}` (not just `{mainClients}`), same as every other wizard
 * sub-form — `taxId` is `@NotBlank` on the backend (Task 2), and every independent save must keep
 * carrying it.
 */
const MainClientsForm = ({ profile, setIsDirty }: MainClientsFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const defaultValues: MainClientsFormFields = useMemo(
    () => ({
      mainClients: (profile.mainClients || []).map((mainClient) => ({
        clientName: mainClient.clientName ?? "",
        contactPersonName: mainClient.contactPersonName ?? "",
        contactEmail: mainClient.contactEmail ?? "",
        contactPhone: mainClient.contactPhone ?? "",
      })),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<MainClientsFormFields>({
    resolver: yupResolver(mainClientsValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as AddressesForm.tsx/
  // BasicInformationForm.tsx — see those files' own docstrings/comments for the full rationale.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "mainClients",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: MainClientsFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      mainClients: data.mainClients,
    } as CompanyProfileRequest);
  };

  return (
    <div className="main-clients-form">
      <div className="main-clients-form__header">
        <h3>{t("companyProfile.mainClients")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyMainClient)}
        >
          {t("companyProfile.addClient")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="main-clients-form__form"
        >
          {fields.length > 0 && (
            <div className="main-clients-form__rows">
              {fields.map((field, index) => (
                <MainClientRow
                  key={field.id}
                  index={index}
                  control={control}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="main-clients-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default MainClientsForm;
